import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './fields.module.css';
import { type SelectroController } from '@aha-app/selectro-v2';
import { type Opt, type SelectFieldProps } from './types';
import MemoSelectro from './MemoSelectro';

const SelectField = <T,>({
  initialValue: rawInitialValue,
  setValue,
  getOptions,
  fetchUrl,
  hideSearchBar,
  resolveOptions,
  branchesSelectable = false,
  ...props
}: SelectFieldProps<T>) => {
  const setValueRef = useRef(setValue);
  const [currentSelected, setCurrentSelected] = useState<
    Opt[] | Opt | undefined
  >();
  const initialValueRef = useRef(rawInitialValue);
  const getOptionsRef = useRef(getOptions);

  const wrappedSetValue = useCallback<typeof setValue>(
    selected => {
      setValueRef.current(props.multiple ? selected : selected[0]);
      const newValue = Array.isArray(selected)
        ? selected.map((item: { text: string; value: string }) => ({
            text: item.text,
            value: item.value,
          }))
        : [{ value: selected.value, text: selected.text }];

      setCurrentSelected(newValue);
    },
    [props.multiple]
  );

  const initialValue = (
    (Array.isArray(initialValueRef.current)
      ? initialValueRef.current
      : [initialValueRef.current]) ?? []
  )
    .filter(Boolean)
    .map(value => ({ value }));

  const callbacks = {
    select: ({ controller }: { controller: SelectroController }) => {
      const newValue =
        controller.selectedItems
          ?.map(item => item.data ?? item)
          .filter(Boolean) ?? [];
      wrappedSetValue(newValue as any);
    },
    clearAll: () => {
      wrappedSetValue([] as any);
    },
  };

  const functions = getOptions
    ? {
        fetch: async ({ searchParam, paginator }) => {
          const options = await getOptionsRef.current!({
            searchParam,
            page: paginator.currentPage + 1,
          });

          // if there is no currentSelected, attempt to set it from the raw value. This is needed
          // in case we prefill the selected value but only know the value, not the text
          if (!currentSelected && initialValue) {
            const selected = initialValue
              .map(({ value }) => {
                const option = options.options.find(opt => opt.value === value);
                if (option?.value && option?.text) {
                  return { value: option.value, text: option.text };
                } else {
                  return null;
                }
              })
              .filter(Boolean);
            setCurrentSelected(selected);
          }

          return options;
        },
      }
    : {};

  const type = props.multiple ? 'multiple' : props.type ?? 'single';

  // if we don't know the selected value's text, we use the resolveOptions callback to retrieve it
  const resolveOptionsRef = useRef(resolveOptions);
  useEffect(() => {
    const val = initialValueRef.current;
    const vals = Array.isArray(val) ? val : [val];
    if (
      val &&
      resolveOptionsRef.current &&
      vals.every(v => typeof v === 'string')
    ) {
      void resolveOptionsRef.current(vals).then(resolvedValue => {
        if (resolvedValue) {
          setCurrentSelected(resolvedValue);
        }
      });
    }
  }, []);

  return (
    <MemoSelectro
      className={styles.selectro}
      callbacks={callbacks}
      selected={currentSelected}
      type={type}
      functions={functions}
      hideSearchBar={hideSearchBar}
      fetchUrl={fetchUrl}
      deferFetch={!!fetchUrl}
      branchesSelectable={branchesSelectable}
      showClearAllButton={props.showClearAll ?? props.multiple}
      {...props}
    />
  );
};

export default SelectField;
